import { useEffect, useRef, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { maplibregl } from '../../shared/maplibre.js'
import 'maplibre-gl/dist/maplibre-gl.css'
import _MB from '@mapbox/mapbox-gl-draw'
const MapboxDraw = (_MB as any).default || _MB
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import { useOrgStore } from '../../shared/store/org.store.js'
import { MAP_TILES_URL, MAP_ATTRIBUTION, MAP_TILE_SIZE } from '../../shared/map/config.js'
import {
  getDeliveryZones,
  createDeliveryZone,
  updateDeliveryZone,
  deleteDeliveryZone,
  type DeliveryZoneResponse,
} from '../../shared/api/delivery-zones.js'
import { getLocation } from '../../shared/api/locations.js'
import type { Location } from '../../shared/types/location.js'
import { ApiError } from '../../shared/types/api.js'
import { zoneColor, DEFAULT_ZONE_COLOR, makeZoneFeature } from '../../shared/delivery-zones/colors.js'

interface ZoneForm {
  name: string
  price: number
  minOrderAmount: number
  freeDeliveryThreshold: string
  eta: string
  priority: number
  isActive: boolean
}

const defaultForm = (): ZoneForm => ({
  name: '', price: 0, minOrderAmount: 0, freeDeliveryThreshold: '', eta: '', priority: 0, isActive: true,
})

const DRAW_STYLES: any[] = [{
  id: 'gl-draw-polygon-fill',
  type: 'fill',
  filter: ['all', ['==', '$type', 'Polygon']],
  paint: { 'fill-color': ['coalesce', ['get', 'color'], DEFAULT_ZONE_COLOR], 'fill-opacity': 0.2 },
}, {
  id: 'gl-draw-polygon-stroke-active',
  type: 'line',
  filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
  layout: { 'line-cap': 'round', 'line-join': 'round' },
  paint: { 'line-color': ['coalesce', ['get', 'color'], DEFAULT_ZONE_COLOR], 'line-dasharray': [0.2, 2], 'line-width': 2 },
}, {
  id: 'gl-draw-polygon-and-line-midpoint',
  type: 'circle',
  filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
  paint: { 'circle-radius': 3, 'circle-color': '#fbbf24' },
}]

type EditorStatus = 'loading' | 'ready' | 'error'
type FormStatus = 'idle' | 'saving' | 'deleting'

interface DrawInstance {
  add: (feature: GeoJSON.Feature) => any
  deleteAll: () => void
  changeMode: (mode: string, options?: any) => void
  getAll: () => GeoJSON.FeatureCollection
  get: (id: string) => GeoJSON.Feature | undefined
}

export default function DeliveryZonesPage() {
  const { t } = useTranslation()
  const { locationId } = useParams<{ locationId: string }>()
  const orgId = useOrgStore(s => s.activeOrganization)?.id
  const mapContainer = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const drawRef = useRef<DrawInstance | null>(null)

  const [status, setStatus] = useState<EditorStatus>('loading')
  const [location, setLocation] = useState<Location | null>(null)
  const [zones, setZones] = useState<DeliveryZoneResponse[]>([])
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null)
  const [form, setForm] = useState<ZoneForm>(defaultForm())
  const [formStatus, setFormStatus] = useState<FormStatus>('idle')
  const [formError, setFormError] = useState('')
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasDrawn, setHasDrawn] = useState(false)

  const initialZonesRef = useRef<DeliveryZoneResponse[]>([])

  useEffect(() => {
    if (!orgId || !locationId) return
    setStatus('loading')
    void Promise.all([
      getLocation(orgId, locationId),
      getDeliveryZones(orgId, locationId),
    ]).then(([loc, zns]) => {
      initialZonesRef.current = zns
      setLocation(loc)
      setZones(zns)
      setStatus('ready')
    }).catch(() => setStatus('error'))
  }, [orgId, locationId])

  // Initialize the map only after the container is rendered (status === 'ready')
  useEffect(() => {
    if (status !== 'ready' || !location || !mapContainer.current || mapRef.current) return
    let disposed = false
    const center: [number, number] = location.latitude && location.longitude
      ? [location.longitude, location.latitude]
      : [37.6, 55.7]
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: { version: 8, sources: { osm: { type: 'raster', tiles: [MAP_TILES_URL], tileSize: MAP_TILE_SIZE, attribution: MAP_ATTRIBUTION } }, layers: [{ id: 'osm', type: 'raster', source: 'osm' }] },
      center,
      zoom: 13,
    })
map.addControl(new maplibregl.NavigationControl(), 'top-left')
      map.on('load', () => {
        if (disposed) return
        try {
          const draw = new (MapboxDraw as any)({
        displayControlsDefault: false,
        controls: { polygon: true, line_string: false, trash: false },
        defaultMode: 'simple_select',
        styles: DRAW_STYLES,
      })
      map.addControl(draw as unknown as maplibregl.IControl, 'top-left')
      if (draw && typeof draw === 'object') {
        drawRef.current = draw as DrawInstance
      }

      // After finishing a polygon — switch to selection and show form
      const drawEvents = (map as any)?.on
      if (typeof drawEvents === 'function') {
        drawEvents('draw.create', (e: { feature: GeoJSON.Feature }) => {
          if (e.feature?.geometry?.type === 'Polygon') {
            draw.changeMode('simple_select')
            setIsDrawing(false)
            setHasDrawn(true)
          }
        })
        // Track drawing mode for visual feedback and help text
        drawEvents('draw.modechange', (e: { mode: string }) => {
          setIsDrawing(e.mode === 'draw_polygon')
        })
      }

      // Load existing zones onto the map as static features
      initialZonesRef.current.forEach(zone => {
        if (zone.geometry?.coordinates?.length) {
          draw.add(makeZoneFeature(zone))
        }
      })
    } catch (e) {
      console.warn('Draw initialisation failed', e)
      setFormError(t('deliveryZones.loadError'))
    }
    })
    mapRef.current = map
    return () => {
      disposed = true
      map.remove()
      mapRef.current = null
      drawRef.current = null
    }
  }, [status, location])

  const clearSelection = useCallback(() => {
    drawRef.current?.deleteAll()
    zones.forEach(zone => {
      if (zone.geometry?.coordinates?.length) {
        drawRef.current?.add(makeZoneFeature(zone))
      }
    })
  }, [zones])

  const startDraw = useCallback(() => {
    setSelectedZoneId(null)
    setForm(defaultForm())
    setFormError('')
    setIsDrawing(true)
    setHasDrawn(false)
    clearSelection()
    drawRef.current?.changeMode('draw_polygon')
  }, [clearSelection])

  const selectZoneForEdit = useCallback((zone: DeliveryZoneResponse) => {
    setSelectedZoneId(zone.id)
    setFormError('')
    setForm({
      name: zone.name,
      price: Number(zone.price),
      minOrderAmount: Number(zone.minOrderAmount),
      freeDeliveryThreshold: zone.freeDeliveryThreshold ? String(zone.freeDeliveryThreshold) : '',
      eta: zone.eta ? String(zone.eta) : '',
      priority: zone.priority,
      isActive: zone.isActive,
    })
    clearSelection()
    // Add this zone as editable
    drawRef.current?.add({ type: 'Feature', id: `editing-${zone.id}`, properties: { color: zoneColor(zone.id) }, geometry: zone.geometry })
    drawRef.current?.changeMode('direct_select', { featureId: `editing-${zone.id}` })
  }, [clearSelection])

  const save = async () => {
    if (!orgId || !locationId) return
    if (!form.name.trim()) { setFormError(t('deliveryZones.nameRequired')); return }

    const features = drawRef.current?.getAll().features ?? []
    let polygonCoords: number[][][] | null = null

    if (selectedZoneId) {
      // Editing existing zone — find the edited feature
      const editFeature = features.find((f: GeoJSON.Feature) => f.id === `editing-${selectedZoneId}`)
      if (editFeature && editFeature.geometry.type === 'Polygon') {
        polygonCoords = (editFeature.geometry as GeoJSON.Polygon).coordinates
      } else {
        // Keep existing geometry
        const z = zones.find(z => z.id === selectedZoneId)
        polygonCoords = z?.geometry.coordinates ?? null
      }
    } else {
      // New zone — find the drawn polygon
      const drawn = features.find((f: GeoJSON.Feature) => f.id && typeof f.id === 'string' && !f.id.startsWith('zone-') && f.geometry.type === 'Polygon')
      if (!drawn) { setFormError(t('deliveryZones.polygonRequired')); return }
      polygonCoords = (drawn.geometry as GeoJSON.Polygon).coordinates
    }

    if (!polygonCoords || !polygonCoords[0] || polygonCoords[0].length < 4) { setFormError(t('deliveryZones.polygonRequired')); return }

    const geometry = { type: 'Polygon' as const, coordinates: polygonCoords }
    setFormStatus('saving')
    setFormError('')

    try {
      if (selectedZoneId) {
        const updated = await updateDeliveryZone(orgId, locationId, selectedZoneId, {
          name: form.name,
          geometry,
          price: form.price,
          minOrderAmount: form.minOrderAmount,
          priority: form.priority,
          isActive: form.isActive,
          ...(form.freeDeliveryThreshold ? { freeDeliveryThreshold: parseFloat(form.freeDeliveryThreshold) } : { freeDeliveryThreshold: null }),
          ...(form.eta ? { eta: parseInt(form.eta) } : {}),
        })
        setZones(prev => prev.map(z => z.id === updated.id ? updated : z))
      } else {
        const created = await createDeliveryZone(orgId, locationId, {
          name: form.name,
          geometry,
          price: form.price,
          minOrderAmount: form.minOrderAmount,
          priority: form.priority,
          ...(form.freeDeliveryThreshold ? { freeDeliveryThreshold: parseFloat(form.freeDeliveryThreshold) } : {}),
          ...(form.eta ? { eta: parseInt(form.eta) } : {}),
        })
        setZones(prev => [...prev, created])
      }
      setHasDrawn(false)
      cancelEditing()
    } catch (e: unknown) {
      setFormError(e instanceof ApiError ? e.message : String(e))
    } finally { setFormStatus('idle') }
  }

  const cancelEditing = useCallback(() => {
    setSelectedZoneId(null)
    setForm(defaultForm())
    setFormError('')
    setIsDrawing(false)
    setHasDrawn(false)
    drawRef.current?.deleteAll()
    drawRef.current?.changeMode('simple_select')
    // Re-add all existing zones as static
    zones.forEach(zone => {
      if (zone.geometry?.coordinates?.length) {
        drawRef.current?.add(makeZoneFeature(zone))
      }
    })
  }, [zones])

  const removeZone = async (zoneId: string, zoneName: string) => {
    if (!orgId || !locationId) return
    if (!window.confirm(t('deliveryZones.confirmDelete', { name: zoneName }))) return
    setFormStatus('deleting')
    try {
      await deleteDeliveryZone(orgId, locationId, zoneId)
      setZones(prev => prev.filter(z => z.id !== zoneId))
      cancelEditing()
    } catch {
      setFormError(t('deliveryZones.deleteError'))
    } finally { setFormStatus('idle') }
  }

  if (status === 'loading') return <div className="spinner" />
  if (status === 'error') return <div className="error-banner">{t('deliveryZones.loadError')}</div>

  return (
    <div className="page">
      <div className="page-header">
        <h1>{location?.name} — {t('deliveryZones.title')}</h1>
      </div>

      {formError && <div className="error-banner">{formError}</div>}

      {isDrawing && <div className="help-banner">{t('deliveryZones.helpDraw')}</div>}

      <div className="delivery-zones-layout">
        <div ref={mapContainer} style={{ flex: 1, height: '500px', borderRadius: 8, overflow: 'hidden', minWidth: 0 }} />

        <div className="delivery-zones-list">
          {!selectedZoneId && !isDrawing && !hasDrawn && (
            <div className="card" style={{ padding: 16, marginBottom: 16 }}>
              <button className="btn btn-primary btn-full" onClick={startDraw} disabled={formStatus !== 'idle'}>
                {t('deliveryZones.addZone')}
              </button>
            </div>
          )}
          {(selectedZoneId || isDrawing || hasDrawn) && (
            <div className="card" style={{ padding: 16, marginBottom: 16 }}>
              <h3>{selectedZoneId ? t('deliveryZones.editZone') : t('deliveryZones.newZone')}</h3>
              <div className="form-group">
                <label>{t('deliveryZones.zoneName')}</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="form-row" style={{ display: 'flex', gap: 8 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>{t('deliveryZones.price')}</label>
                  <input type="number" min="0" value={form.price} onChange={e => setForm(p => ({ ...p, price: Number(e.target.value) }))} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>{t('deliveryZones.minOrder')}</label>
                  <input type="number" min="0" value={form.minOrderAmount} onChange={e => setForm(p => ({ ...p, minOrderAmount: Number(e.target.value) }))} />
                </div>
              </div>
              <div className="form-row" style={{ display: 'flex', gap: 8 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>{t('deliveryZones.freeThreshold')}</label>
                  <input type="number" min="0" value={form.freeDeliveryThreshold} onChange={e => setForm(p => ({ ...p, freeDeliveryThreshold: e.target.value }))} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>{t('deliveryZones.eta')}</label>
                  <input type="number" min="0" value={form.eta} onChange={e => setForm(p => ({ ...p, eta: e.target.value }))} />
                </div>
              </div>
              <div className="form-row" style={{ display: 'flex', gap: 8 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>{t('deliveryZones.priority')}</label>
                  <input type="number" min="0" value={form.priority} onChange={e => setForm(p => ({ ...p, priority: Number(e.target.value) }))} />
                </div>
                {selectedZoneId && (
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>{t('deliveryZones.isActive')}</label>
                    <select value={form.isActive ? 'true' : 'false'} onChange={e => setForm(p => ({ ...p, isActive: e.target.value === 'true' }))}>
                      <option value="true">{t('active')}</option>
                      <option value="false">{t('inactive')}</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="form-actions" style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="btn btn-primary" onClick={save} disabled={formStatus === 'saving'}>
                  {formStatus === 'saving' ? t('saving') : t('save')}
                </button>
                <button className="btn btn-ghost" onClick={cancelEditing}>{t('cancel')}</button>
              </div>
            </div>
          )}

          <h3>{t('deliveryZones.savedZones')}</h3>
          {zones.length === 0 && (
            <div className="empty-state">
              <p className="text-muted">{t('deliveryZones.noZones')}</p>
              <p className="text-muted">{t('deliveryZones.emptyHint')}</p>
            </div>
          )}
          {zones.map(zone => (
            <div key={zone.id} className="card" style={{ padding: 12, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>{zone.name}</strong>
                <span className={`badge ${zone.isActive ? 'badge-success' : 'badge-muted'}`}>
                  {zone.isActive ? t('active') : t('inactive')}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <span className="zone-swatch" style={{ background: zoneColor(zone.id) }} />
                <div style={{ fontSize: 13, color: '#666' }}>
                  {t('deliveryZones.price')}: {Number(zone.price)} ₽ | {t('deliveryZones.minOrder')}: {Number(zone.minOrderAmount)} ₽
                  {zone.eta != null && ` | ETA: ${zone.eta} ${t('minutes')}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn btn-sm" onClick={() => selectZoneForEdit(zone)}>{t('edit')}</button>
                <button className="btn btn-sm btn-danger" onClick={() => removeZone(zone.id, zone.name)} disabled={formStatus === 'deleting'}>{t('delete')}</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}